import type {
  CheckoutPaymentProvider,
  CheckoutPaymentResult,
  CheckoutSubmissionPayload,
  StripeCheckoutStatusResult,
} from './checkout.types';
import {
  getCheckoutSuccessMessage,
  parseCheckoutPaymentInstructions,
} from './checkoutPaymentInstructions.utils';
import config from '../../config/environment';
import { CHECKOUT_PROCESSING_ERROR_MESSAGE, CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE } from './checkout.constants';
import { toPublicCheckoutErrorMessage } from './checkout.utils';
import { withRequestIdHeaders } from '../../utils/requestId';

interface StudentCheckoutResponse {
  success?: boolean;
  data?: {
    studentId?: number;
    updateActionToken?: string;
    onboardingToken?: string | null;
    payment?: {
      subscription?: {
        id?: string | null;
        url?: string | null;
        initialPayment?: {
          id?: string | null;
        } | null;
      };
      checkoutInstructions?: unknown;
      stripeCheckout?: {
        sessionId?: string;
        clientSecret?: string;
      } | null;
      initialPayment?: {
        id?: string | null;
      } | null;
    };
  };
  error?: {
    code?: string;
    message?: string;
  } | string;
}

interface StripeCheckoutStatusResponse {
  success?: boolean;
  data?: {
    sessionStatus?: 'open' | 'complete' | 'expired' | null;
    paymentStatus?: 'unpaid' | 'paid' | 'no_payment_required' | null;
    paid?: boolean;
    studentId?: number | null;
    onboardingToken?: string | null;
    externalReference?: string | null;
    externalPaymentId?: string | null;
  };
  error?: StudentCheckoutResponse['error'];
}

const CHECKOUT_IDEMPOTENCY_STORAGE_KEY = 'checkout:idempotency-key';

function resolveCheckoutApiErrorMessage(body: StudentCheckoutResponse): string {
  const { error } = body;
  let message = CHECKOUT_PROCESSING_ERROR_MESSAGE;

  if (typeof error === 'string' && error.trim()) {
    message = error.trim();
  } else if (error && typeof error === 'object' && error.message?.trim()) {
    message = error.message.trim();
  }

  return toPublicCheckoutErrorMessage(message, CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE);
}

function resolveCheckoutErrorCode(body: StudentCheckoutResponse): string | null {
  if (body.error && typeof body.error === 'object' && typeof body.error.code === 'string') {
    return body.error.code.trim() || null;
  }

  return null;
}

function resolveCheckoutContinuation(
  body: StudentCheckoutResponse,
): CheckoutPaymentResult['continuation'] {
  const studentId = typeof body.data?.studentId === 'number' ? body.data.studentId : null;
  const updateActionToken = typeof body.data?.updateActionToken === 'string'
    ? body.data.updateActionToken.trim()
    : '';

  if (!studentId || studentId <= 0 || !updateActionToken) {
    return null;
  }

  return {
    studentId,
    updateActionToken,
  };
}

function peekCheckoutIdempotencyKey(): string | null {
  try {
    const existing = sessionStorage.getItem(CHECKOUT_IDEMPOTENCY_STORAGE_KEY)?.trim();
    return existing || null;
  } catch {
    return null;
  }
}

function resolveCheckoutIdempotencyKey(): string {
  try {
    const existing = sessionStorage.getItem(CHECKOUT_IDEMPOTENCY_STORAGE_KEY)?.trim();
    if (existing) {
      return existing;
    }

    const nextKey = crypto.randomUUID();
    sessionStorage.setItem(CHECKOUT_IDEMPOTENCY_STORAGE_KEY, nextKey);
    return nextKey;
  } catch {
    return crypto.randomUUID();
  }
}

export function clearCheckoutIdempotencyKey(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_IDEMPOTENCY_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}

/**
 * Default checkout payment provider.
 * Replace or extend when wiring a real gateway (Stripe, Mercado Pago, Asaas, etc.).
 */
export class PortalCheckoutPaymentProvider implements CheckoutPaymentProvider {
  id = 'portal-default';

  name = 'Portal Checkout';

  async processCheckout(payload: CheckoutSubmissionPayload): Promise<CheckoutPaymentResult> {
    const idempotencyKey = resolveCheckoutIdempotencyKey();

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: withRequestIdHeaders({
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${config.supabaseAnonKey}`,
        'Idempotency-Key': idempotencyKey,
      }),
      body: JSON.stringify({
        action: 'checkout',
        payload,
      }),
    });

    const body = await response.json().catch(() => ({})) as StudentCheckoutResponse;

    if (!response.ok || body.success === false) {
      const errorCode = resolveCheckoutErrorCode(body);
      // Keep the key only while the same attempt is still in-flight / conflicting.
      // Failed payments must allow an immediate retry with a fresh key.
      if (errorCode !== 'CHECKOUT_IN_PROGRESS' && errorCode !== 'IDEMPOTENCY_CONFLICT') {
        clearCheckoutIdempotencyKey();
      }

      return {
        success: false,
        message: resolveCheckoutApiErrorMessage(body),
        continuation: resolveCheckoutContinuation(body),
      };
    }

    const stripeSessionId = body.data?.payment?.stripeCheckout?.sessionId?.trim() ?? '';
    const stripeClientSecret = body.data?.payment?.stripeCheckout?.clientSecret?.trim() ?? '';
    const stripeCheckout = stripeSessionId && stripeClientSecret
      ? { sessionId: stripeSessionId, clientSecret: stripeClientSecret }
      : null;

    if (!stripeCheckout) {
      clearCheckoutIdempotencyKey();
    }

    const paymentInstructions = parseCheckoutPaymentInstructions(
      body.data?.payment?.checkoutInstructions,
    );
    const initialPaymentId = body.data?.payment?.initialPayment?.id
      ?? body.data?.payment?.subscription?.initialPayment?.id;
    const externalPaymentId = (
      paymentInstructions?.externalPaymentId
      || (typeof initialPaymentId === 'string' ? initialPaymentId.trim() : '')
      || null
    );

    const onboardingToken = typeof body.data?.onboardingToken === 'string'
      ? body.data.onboardingToken.trim()
      : '';
    const studentId = typeof body.data?.studentId === 'number' && body.data.studentId > 0
      ? body.data.studentId
      : null;

    return {
      success: true,
      message: stripeCheckout
        ? 'Preencha os dados de pagamento no ambiente seguro do Stripe.'
        : getCheckoutSuccessMessage(paymentInstructions, false, payload.paymentMethod),
      externalReference: body.data?.payment?.subscription?.id ?? `checkout-${body.data?.studentId ?? Date.now()}`,
      externalPaymentId,
      paymentInstructions,
      studentId,
      onboardingToken: onboardingToken || null,
      stripeCheckout,
    };
  }

  async getStripeCheckoutStatus(sessionId: string): Promise<StripeCheckoutStatusResult> {
    const idempotencyKey = peekCheckoutIdempotencyKey();
    if (!idempotencyKey) {
      return {
        success: false,
        message: 'Não foi possível validar a tentativa de pagamento Stripe.',
        sessionStatus: null,
        paymentStatus: null,
      };
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: withRequestIdHeaders({
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${config.supabaseAnonKey}`,
        'Idempotency-Key': idempotencyKey,
      }),
      body: JSON.stringify({
        action: 'checkout-stripe-status',
        payload: { sessionId },
      }),
    });
    const body = await response.json().catch(() => ({})) as StripeCheckoutStatusResponse;

    if (!response.ok || body.success === false) {
      return {
        success: false,
        message: resolveCheckoutApiErrorMessage(body as StudentCheckoutResponse),
        sessionStatus: null,
        paymentStatus: null,
      };
    }

    const paid = body.data?.paid === true;
    return {
      success: true,
      message: paid
        ? 'Pagamento confirmado com sucesso.'
        : 'Seu pagamento ainda está sendo confirmado pelo Stripe.',
      sessionStatus: body.data?.sessionStatus ?? null,
      paymentStatus: body.data?.paymentStatus ?? null,
      paymentResult: paid
        ? {
          success: true,
          message: 'Pagamento confirmado com sucesso.',
          externalReference: body.data?.externalReference ?? undefined,
          externalPaymentId: body.data?.externalPaymentId ?? null,
          studentId: body.data?.studentId ?? null,
          onboardingToken: body.data?.onboardingToken ?? null,
        }
        : null,
    };
  }
}

export const defaultCheckoutPaymentProvider = new PortalCheckoutPaymentProvider();
